import React from "react";

/**
 * Standardized color palette for status badges across the grants module.
 * - amber/yellow = new/pending (nowy, oczekuje)
 * - green = success/fetched (pobrano, aktywne)
 * - blue = skipped/info (pominięty, wznowiony)
 * - red = error/failed
 * - gray = inactive/unknown/none
 * - orange = cancelled/revoked (odwołany - business event, not system error)
 * - purple = results/outcomes (wyniki ogłoszone)
 * - indigo = extended/modified (termin przedłużony)
 */
export const STATUS_COLORS = {
  new: "bg-amber-100 text-amber-800",
  pending: "bg-amber-100 text-amber-800",
  fetched: "bg-green-100 text-green-800",
  success: "bg-green-100 text-green-800",
  active: "bg-green-100 text-green-800",
  error: "bg-red-100 text-red-800",
  inactive: "bg-gray-100 text-gray-800",
  unknown: "bg-gray-100 text-gray-800",
  // Analysis status badges
  "analysis-none": "bg-gray-100 text-gray-800",
  "analysis-pending": "bg-amber-100 text-amber-800",
  "analysis-completed": "bg-green-100 text-green-800",
  "analysis-skipped": "bg-blue-100 text-blue-800",
  "analysis-failed": "bg-red-100 text-red-800",
  // Extraction status badges (grant call lifecycle)
  // @see docs/specifications/2026-01-16-extraction-schema-refactoring.md
  "extraction-OPENED": "bg-green-100 text-green-800",     // Nabór otwarty
  "extraction-CLOSED": "bg-gray-100 text-gray-800",       // Nabór zamknięty (normalnie)
  // CANCELLED is orange to differentiate from errors (red)
  // CANCELLED = business event (grant was revoked), not a system error
  "extraction-CANCELLED": "bg-orange-100 text-orange-800", // Nabór odwołany
  "extraction-SUSPENDED": "bg-amber-100 text-amber-800",  // Nabór wstrzymany
  "extraction-RESUMED": "bg-blue-100 text-blue-800",      // Nabór wznowiony
  "extraction-EXTENDED": "bg-indigo-100 text-indigo-800", // Termin przedłużony
  // UNDER_REVIEW is cyan (in-progress evaluation, before results)
  "extraction-UNDER_REVIEW": "bg-cyan-100 text-cyan-800", // Nabór rozpatrywany
  "extraction-RESULTS": "bg-purple-100 text-purple-800",  // Wyniki ogłoszone
  "extraction-UPDATE": "bg-gray-100 text-gray-800",       // Aktualizacja
  // Feature lifecycle badges (for sidebar/navigation)
  "feature-beta": "bg-orange-100 text-orange-700",
  "feature-new": "bg-blue-100 text-blue-700",
  "feature-deprecated": "bg-neutral-100 text-neutral-500",
  // robots.txt status badges
  // @see docs/specifications/2026-05-20-robots-txt-source-status/spec.md
  "robots-ALLOWED": "bg-green-100 text-green-800",
  "robots-DISALLOWED": "bg-red-100 text-red-800",
  "robots-NO_ROBOTS_TXT": "bg-gray-100 text-gray-800",
  "robots-ERROR": "bg-yellow-100 text-yellow-800",
  "robots-NOT_CHECKED": "bg-gray-100 text-gray-800",
  "robots-NOT_APPLICABLE": "bg-gray-100 text-gray-800",
} as const;

export type StatusType = keyof typeof STATUS_COLORS;

/** Feature lifecycle badge types for sidebar/navigation */
export type FeatureBadgeType = "feature-beta" | "feature-new" | "feature-deprecated";

/** Labels for feature badges */
export const FEATURE_BADGE_LABELS: Record<FeatureBadgeType, string> = {
  "feature-beta": "BETA",
  "feature-new": "NOWY",
  "feature-deprecated": "WYCOFANE",
};

/**
 * Get tooltip suffix for feature badge (for collapsed sidebar)
 * @param badge - Feature badge type or undefined
 * @returns Suffix like " (BETA)" or empty string
 */
export function getFeatureBadgeTooltipSuffix(badge?: FeatureBadgeType): string {
  if (!badge) return "";
  const label = FEATURE_BADGE_LABELS[badge];
  return label ? ` (${label})` : "";
}

/**
 * Polish labels for status types, used for aria-label accessibility.
 */
export const STATUS_ARIA_LABELS: Record<StatusType, string> = {
  new: "Status: Nowy",
  pending: "Status: Oczekuje",
  fetched: "Status: Pobrano",
  success: "Status: Sukces",
  active: "Status: Aktywny",
  error: "Status: Błąd",
  inactive: "Status: Nieaktywny",
  unknown: "Status: Nieznany",
  "analysis-none": "Status analizy: Brak",
  "analysis-pending": "Status analizy: Oczekuje",
  "analysis-completed": "Status analizy: Zakończona",
  "analysis-skipped": "Status analizy: Pominięta",
  "analysis-failed": "Status analizy: Niepowodzenie",
  "extraction-OPENED": "Status naboru: Otwarty",
  "extraction-CLOSED": "Status naboru: Zamknięty",
  "extraction-CANCELLED": "Status naboru: Odwołany",
  "extraction-SUSPENDED": "Status naboru: Wstrzymany",
  "extraction-RESUMED": "Status naboru: Wznowiony",
  "extraction-EXTENDED": "Status naboru: Przedłużony",
  "extraction-UNDER_REVIEW": "Status naboru: Rozpatrywany",
  "extraction-RESULTS": "Status naboru: Wyniki ogłoszone",
  "extraction-UPDATE": "Status naboru: Aktualizacja",
  "feature-beta": "Status modułu: Beta",
  "feature-new": "Status modułu: Nowy",
  "feature-deprecated": "Status modułu: Wycofany",
  "robots-ALLOWED": "Dozwolony przez robots.txt",
  "robots-DISALLOWED": "Zablokowany przez robots.txt",
  "robots-NO_ROBOTS_TXT": "Brak pliku robots.txt (traktowane jako dozwolone)",
  "robots-ERROR": "Błąd sprawdzenia robots.txt",
  "robots-NOT_CHECKED": "Jeszcze nie sprawdzono",
  "robots-NOT_APPLICABLE": "Brak URL do sprawdzenia",
};

interface StatusBadgeProps {
  /** Status type determining the badge color */
  status: StatusType;
  /** Text to display in the badge */
  label: string;
  /** Optional additional CSS classes */
  className?: string;
  /** Optional accessibility label for screen readers (Polish translation) */
  "aria-label"?: string;
  /** Optional tooltip text shown on hover (Polish explanation of status) */
  title?: string;
  /** Optional test ID for E2E testing */
  "data-testid"?: string;
  /** Optional element id — e.g. to target this badge from another element's `aria-describedby` */
  id?: string;
}

/**
 * Reusable status badge component with consistent styling.
 * Used across grants pages to indicate article/source status.
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label,
  className = "",
  "aria-label": ariaLabel,
  title,
  "data-testid": dataTestId,
  id,
}) => {
  const colorClasses = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  const defaultAriaLabel = STATUS_ARIA_LABELS[status] || STATUS_ARIA_LABELS.unknown;

  return (
    <span
      id={id}
      role="status"
      aria-label={ariaLabel || defaultAriaLabel}
      title={title}
      data-testid={dataTestId}
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClasses} ${className}`}
    >
      {label}
    </span>
  );
};
